import { defineStore } from 'pinia';
import { Get, type ListResponse } from '@/scripts/api';
import { useLS } from '@/scripts/utils';
import type { Year } from '@sas/shared/database/dbo';

/**
 * Session-persistent cache of the active policy year. Backed by localStorage so the value is
 * available instantly on reload without waiting for the OData round-trip. `load()` refreshes in
 * background — callers render from the cached value and the reactive update propagates when the
 * fresh response arrives.
 */
export const useYearStore = defineStore('year', {
  state: () => ({
    activeYear: useLS<Year | null>('sas:admin:activeYear', null),
  }),
  actions: {
    /**
     * Refreshes the cached active year. Mirrors `dbo.udf_ActiveYear()` exactly — `TOP 1 WHERE
     * YER_Active = 1 ORDER BY YER_Year DESC` — so the admin UI and the public site never disagree
     * about which year is live. The `$orderby` is load-bearing: without it, a database with more
     * than one active row returns an arbitrary one.
     *
     * Note `MAX(YER_Year)` is deliberately NOT the definition. `usp_RolloverYear` creates year
     * `max+1` inactive, so the greatest year is routinely one that has not gone live yet.
     */
    async load(): Promise<void> {
      try {
        const res = (await Get(
          '/Year',
          '$filter=YER_Active eq true&$orderby=YER_Year desc&$first=1',
        )) as ListResponse<Year> | null;
        const year = res?.value?.[0];
        if (year) this.activeYear = year;
      } catch {
        // Keep stale cached value silently.
      }
    },
  },
});
